import { aboutData } from "@/mocks/data";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useRef, useState } from "react";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于私享观影指南",
  description: aboutData.introduction,
  mainEntity: {
    "@type": "Organization",
    name: aboutData.brandName,
    description: aboutData.introduction,
    contactPoint: {
      "@type": "ContactPoint",
      email: aboutData.contact.email,
      telephone: aboutData.contact.phone,
      contactType: "customer service",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "北京",
      addressCountry: "CN",
      streetAddress: "朝阳区建国路88号SOHO现代城A座",
    },
  },
};

export default function AboutPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<{ type: "success" | "error" | null; message: string }>({
    type: null,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const formUrl = "https://readdy.ai/api/form/d9q8gome3oq4jqumti3g";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: null, message: "" });

    const form = formRef.current;
    if (!form) {
      setSubmitting(false);
      return;
    }

    const formData = new FormData(form);
    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus({ type: "success", message: "您的留言已成功提交，我们会尽快回复！" });
      setSubmitting(false);
      return;
    }
    formData.delete("phone_alt");

    const textareaVal = (formData.get("message") as string) || "";
    if (textareaVal.length > 500) {
      setFormStatus({ type: "error", message: "留言内容不能超过500个字符" });
      setSubmitting(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus({ type: "success", message: "您的留言已成功提交，我们会尽快回复！" });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败";
        if (serverMsg && (serverMsg.includes("spam") || serverMsg.includes("form data is spam"))) {
          setFormStatus({ type: "error", message: "提交失败，请稍后再试" });
        } else {
          setFormStatus({ type: "error", message: serverMsg });
        }
      }
    } catch {
      setFormStatus({ type: "error", message: "网络错误，请稍后再试" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 私享观影指南 | 成人影院专业资讯平台"
        description={aboutData.introduction}
        keywords="成人影院,关于我们,私享观影指南,成人影院资讯平台,观影指南品牌"
        ogType="website"
        schemaJson={aboutSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />

            <section className="mt-8 mb-16">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                关于我们
              </h1>
              <p className="mt-4 text-foreground-600 text-sm leading-relaxed max-w-3xl">
                {aboutData.slogan}
              </p>
            </section>

            <section className="mb-16">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {aboutData.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500">
                      {stat.value}
                    </div>
                    <div className="mt-2 text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-6">
                品牌介绍
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  {aboutData.introduction}
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-6">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {aboutData.values.map((value) => (
                  <div
                    key={value.title}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                    联系方式
                  </h3>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <i className="ri-mail-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                      <span>{aboutData.contact.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <i className="ri-phone-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                      <span>{aboutData.contact.phone}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <i className="ri-map-pin-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                      <span>{aboutData.contact.address}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-foreground-600">
                      <i className="ri-wechat-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                      <span>微信：{aboutData.contact.wechat}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                    在线留言
                  </h3>
                  <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
                    <div className="flex flex-col gap-4">
                      <div>
                        <label className="block text-xs text-foreground-500 mb-1">姓名</label>
                        <input
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-500 mb-1">邮箱</label>
                        <input
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-500 mb-1">留言内容</label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                          placeholder="请输入您的留言内容（不超过500字）"
                        ></textarea>
                      </div>
                      <div className="honeypot-field">
                        <input
                          name="phone_alt"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        {submitting ? "提交中..." : "提交留言"}
                      </button>
                    </div>

                    {formStatus.type && (
                      <div
                        className={`mt-4 text-sm p-3 rounded-md ${
                          formStatus.type === "success"
                            ? "bg-accent-100 text-accent-800 border border-accent-200"
                            : "bg-primary-100 text-primary-800 border border-primary-200"
                        }`}
                      >
                        {formStatus.message}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}